/* eslint-disable react/forbid-component-props */
/* eslint-disable no-restricted-imports */
import { Box, Grid, GridProps, IconButton, Typography } from '@mui/material';
import { FC, MouseEventHandler, useEffect, useMemo, useState } from 'react';
import { Price, ProductType } from '@/data/types/Product';
import { RenderProductImg } from '@/components/blocks/RenderProductImg';
import { Img } from '@/components/blocks/MaterialImage';
import { formatPrice } from 'integration/utils/formatPrice';
import {
	productPLPAddToCartButtonSX,
	productPLPContentSX,
	productPLPDiscountSX,
	productPLPFavoriteButtonSX,
	productPLPImageSX,
	productPLPPriceSX,
	productPLPSizeSX,
	productPLPSubtitleSX,
	productPLPTitleSX,
} from '@/components/content/CatalogEntryList/styles/typography';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { combineSX } from '@/utils/combineSX';
import { useProductEvents } from '@/data/Content/_ProductEvents';
import { Linkable } from '@/components/blocks/Linkable';
import { extractNumberFromString } from 'integration/utils/calculateProductPrices';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { mergeRibbonsForProduct } from '@/utils/mergeRibbonsForProduct';
import { ProductRibbons } from '@/components/blocks/Ribbons';

interface InfoElectronico {
	offerDeContado: string | undefined;
	strikedDeContado: string | undefined; // EL QUE SE DEBE DE TACHAR
}

export interface ProductCardPLPProps extends GridProps {
	product: ProductType;
	clickAction?: () => void;
	isFavorite?: boolean;
	infoElectronico?: InfoElectronico;
	onToggleFavorite?: MouseEventHandler<HTMLButtonElement>;
	onAddToCart?: MouseEventHandler<HTMLButtonElement>;
}

export const ProductCardPLP: FC<ProductCardPLPProps> = ({
	product,
	isFavorite = false,
	onToggleFavorite,
	infoElectronico,
	onAddToCart,
	clickAction,
	...rest
}) => {
	const { onClick } = useProductEvents({ product });
	const [isClick, setIsClick] = useState(false);
	const ribbons = mergeRibbonsForProduct(product.ribbons);
	const size = findProductAttribute(product.attributes, 'Talla')?.value;

	useEffect(() => {
		const timeout = setTimeout(() => {
			setIsClick(false);
		}, 1000);
		return () => clearTimeout(timeout);
	}, [isFavorite]);

	const handleFavoriteClick = (e: any) => {
		e.preventDefault();
		setIsClick(true);
		onToggleFavorite?.(e);
	};

	const { discount, price } = useMemo(() => {
		if (infoElectronico) {
			return {
				discount: infoElectronico.strikedDeContado
					? formatPrice(infoElectronico.offerDeContado || '', 'MXN')
					: null,
				price: formatPrice(
					infoElectronico.strikedDeContado || infoElectronico.offerDeContado || '',
					'MXN'
				),
			};
		}

		const rawPrice: Price = product.price.filter((item: Price) => item.usage === 'Display')[0];
		const rawDiscount: Price = product.price.filter((item: Price) => item.usage === 'Offer')[0];

		const listPrice = Number(rawPrice.value);
		const offerPrice = Number(rawDiscount.value);

		const withAdditinalOffer = product.ribbons.find((ribbon) => ribbon.identifier === 'DPV_Oferta');
		const discoundNumber = extractNumberFromString(withAdditinalOffer?.value ?? '');
		const newOffer = offerPrice - (offerPrice * discoundNumber) / 100;

		// Si el precio de oferta es diferente al de lista, tiene descuento
		const discount = newOffer !== listPrice ? formatPrice(newOffer, rawDiscount.currency) : null;
		const price = formatPrice(rawPrice.value, rawPrice.currency);

		return { discount, price };
	}, [product.price, infoElectronico, product.ribbons]);

	const sxPrice = productPLPPriceSX(discount !== null);

	return (
		<Grid
			className="product-card-plp-container"
			item
			xs={6}
			md={4}
			{...rest}
			id={product.partNumber}
		>
			<Box
				className="product-card-plp-content"
				border="1px solid #DADCE0"
				onClick={onClick(clickAction)}
			>
				<Linkable href={`${product?.seo?.href}`} className="product-card-plp-linkable">
					<RenderProductImg
						fullImageRaw={product.thumbnail}
						render={(src) => (
							<Grid className="product-card-plp-image-frame" sx={{ position: 'relative' }}>
								<ProductRibbons ribbons={ribbons} />

								{onToggleFavorite ? (
									<IconButton
										disabled={isClick}
										sx={productPLPFavoriteButtonSX}
										onClick={handleFavoriteClick}
									>
										{isFavorite ? (
											<FavoriteIcon color="primary" />
										) : (
											<FavoriteBorderIcon color="primary" />
										)}
									</IconButton>
								) : null}
								<Img
									className="product-card-plp-image"
									src={src}
									sx={productPLPImageSX(isFavorite)}
									alt={product.name}
								/>
							</Grid>
						)}
					/>
					<Box sx={productPLPContentSX(isFavorite)}>
						{isFavorite ? (
							<>
								<Box>
									<Typography sx={productPLPTitleSX(isFavorite)}>{product.name}</Typography>
									<Typography
										sx={combineSX([productPLPSubtitleSX, { fontWeight: '300', mb: '4px' }])}
									>
										{product.manufacturer}
									</Typography>
									{size ? <Typography sx={productPLPSizeSX}>{`Talla ${size}`}</Typography> : null}
									<Box display={'flex'} flexDirection={'row'} flexWrap={'wrap'}>
										{discount ? (
											<Typography sx={productPLPDiscountSX}>{discount}</Typography>
										) : null}
										<Typography sx={sxPrice}>{price}</Typography>
									</Box>
								</Box>
								<DpButton
									sx={productPLPAddToCartButtonSX}
									size="small"
									fullWidth
									disabled={!size}
									onClick={onAddToCart}
								>
									{'Agregar al carrito'}
								</DpButton>
							</>
						) : (
							<>
								<Box>
									<Typography sx={productPLPTitleSX(isFavorite)}>{product.name}</Typography>
									<Typography sx={combineSX([productPLPSubtitleSX, { fontWeight: '300' }])}>
										{product.manufacturer}
									</Typography>
								</Box>
								<Box display={'flex'} flexDirection={'row'} flexWrap={'wrap'}>
									{discount ? <Typography sx={productPLPDiscountSX}>{discount}</Typography> : null}
									<Typography sx={sxPrice}>{price}</Typography>
								</Box>
							</>
						)}
					</Box>
				</Linkable>
			</Box>
		</Grid>
	);
};
